import { getToken } from '@library/token'
import type { HubConnection } from '@microsoft/signalr'
import {
  HttpTransportType,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr'
import type { AnyAction } from '@reduxjs/toolkit'
import { logIfNotTest } from 'common/helpers'
import type { Middleware } from 'redux'

import type { Hubs } from '../../signalR'
import { addConnection, updateConnection } from '../../signalR'
import { getStore } from '../../store'
import { isSignalRAction } from './asserts'
import { getActionForNotify, getNotifyMethods } from './methodActionsMap'

const hubs = new Map<Hubs, HubConnection>()

export const signalRMiddleware: Middleware = () => {
  return next => async (action: AnyAction | undefined) => {
    if (!action) return

    if (!isSignalRAction(action)) {
      return next(action)
    }
    const actionTypeParts = action.type.split('/')

    const { data, url, hub, request, methodName } = action.payload

    const onStartConnecting = async () => {
      let connection = hubs.get(hub)
      if (!connection) {
        connection = new HubConnectionBuilder()
          .withUrl(url, {
            transport: HttpTransportType.WebSockets,
            accessTokenFactory: () => getToken() ?? 'no token',
          })
          .withAutomaticReconnect()
          .configureLogging(LogLevel.Information)
          .build()

        hubs.set(hub, connection)
      }

      const methods = getNotifyMethods(hub)
      if (methods.length !== 0) {
        for (const notifyMethod of methods) {
          connection.on(notifyMethod, data => {
            logIfNotTest(notifyMethod, data)
            const actions = getActionForNotify(hub, notifyMethod)?.(data)
            if (Array.isArray(actions)) {
              for (const notifyAction of actions) {
                getStore().dispatch(notifyAction)
              }
            }
          })
        }
      }

      connection.onclose(() => {
        logIfNotTest(`Соединение c ${hub} SignalR закрыто`)
      })

      connection.onreconnecting(() => {
        logIfNotTest(`Соединение c ${hub} SignalR восстанавливается...`)
      })

      connection.onreconnected(() => {
        logIfNotTest(`Соединение c ${hub} SignalR восстановлено`)
        if (connection.state === HubConnectionState.Connected) {
          getStore().dispatch(
            updateConnection({
              id: url,
              changes: {
                connectionId: connection.connectionId,
              },
            }),
          )
        }
      })
      try {
        if (connection.state === HubConnectionState.Disconnected) {
          await connection.start()
          const info = { url, connectionId: connection.connectionId }
          getStore().dispatch(addConnection(info))
        }
        logIfNotTest(`Соединение c ${hub} SignalR установлено`)
      } catch (err) {
        logIfNotTest(`Произошла ошибка SignalR c ${hub} `, err)
      }
    }

    const onGetStream = (...args: unknown[]) => {
      if (!methodName) return

      hubs
        .get(hub)
        ?.stream(methodName, ...args)
        .subscribe({
          next: item => {
            //store.dispatch(getActionForMethod(url, methodName)(item))
            logIfNotTest('Stream item', item)
          },
          complete: () => {
            logIfNotTest('Stream completed')
          },
          error: err => {
            logIfNotTest(`Stream error c ${hub} methodName:${methodName}`, err)
          },
        })
    }

    const onGetSync = async (...args: unknown[]) => {
      if (!methodName) return

      try {
        const data = await hubs.get(hub)?.invoke<unknown>(methodName, ...args)
        //store.dispatch(getActionForMethod(url, methodName)(data))
        logIfNotTest('sync values', data)
      } catch (err) {
        logIfNotTest(`Произошла ошибка SignalR c ${hub} methodName:${methodName}`, err)
      }
    }

    const onGetAsync = async (...args: unknown[]) => {
      if (!methodName) return

      try {
        await hubs.get(hub)?.send(methodName, ...args)
      } catch (err) {
        logIfNotTest(`Произошла ошибка SignalR c ${hub} methodName:${methodName}`, err)
      }
    }

    const actionName = actionTypeParts[1]
    if (actionName === 'startConnecting') {
      // await onStartConnecting()
      const connection = hubs.get(hub)
      //если уже есть связь с этим url, не устанавливать другую
      if (!connection || connection.state !== HubConnectionState.Connected) {
        await onStartConnecting()
      }
    } else if (request === 'stream') {
      onGetStream(...data)
    } else if (request === 'sync') {
      await onGetSync(...data)
    } else if (request === 'async') {
      await onGetAsync(...data)
    }

    next(action)
  }
}
